import {
  date,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";

export const showcaseSessions = pgTable("showcase_sessions", {
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  id: varchar("id", { length: 64 }).primaryKey(),
});

export const showcaseRooms = pgTable("showcase_rooms", {
  baseRate: numeric("base_rate", { precision: 10, scale: 2 }).notNull(),
  capacity: integer("capacity").notNull(),
  descriptionEn: text("description_en").notNull(),
  descriptionLv: text("description_lv").notNull(),
  id: uuid("id").defaultRandom().primaryKey(),
  nameEn: text("name_en").notNull(),
  nameLv: text("name_lv").notNull(),
  photosHeritage: jsonb("photos_heritage")
    .$type<string[]>()
    .notNull()
    .default([]),
  photosModern: jsonb("photos_modern").$type<string[]>().notNull().default([]),
  roomType: varchar("room_type", { length: 32 }).notNull(),
  slug: varchar("slug", { length: 64 }).notNull().unique(),
});

export const showcaseAddons = pgTable("showcase_addons", {
  descriptionEn: text("description_en").notNull(),
  descriptionLv: text("description_lv").notNull(),
  id: uuid("id").defaultRandom().primaryKey(),
  nameEn: text("name_en").notNull(),
  nameLv: text("name_lv").notNull(),
  price: numeric("price", { precision: 10, scale: 2 }).notNull(),
  slug: varchar("slug", { length: 64 }).notNull().unique(),
});

export const showcasePromos = pgTable("showcase_promos", {
  code: varchar("code", { length: 32 }).primaryKey(),
  minNights: integer("min_nights").notNull().default(1),
  type: varchar("type", { length: 16 }).notNull(), // "percent" | "fixed"
  validFrom: date("valid_from").notNull(),
  validTo: date("valid_to").notNull(),
  value: numeric("value", { precision: 10, scale: 2 }).notNull(),
});

export const showcaseSeasonalRates = pgTable("showcase_seasonal_rates", {
  endDate: date("end_date").notNull(),
  id: uuid("id").defaultRandom().primaryKey(),
  labelEn: text("label_en").notNull(),
  labelLv: text("label_lv").notNull(),
  multiplier: numeric("multiplier", { precision: 4, scale: 2 }).notNull(),
  startDate: date("start_date").notNull(),
  weekendOnly: integer("weekend_only").notNull().default(0), // 0/1
});

export const showcaseFaq = pgTable("showcase_faq", {
  answerEn: text("answer_en").notNull(),
  answerLv: text("answer_lv").notNull(),
  id: uuid("id").defaultRandom().primaryKey(),
  questionEn: text("question_en").notNull(),
  questionLv: text("question_lv").notNull(),
  tags: jsonb("tags").$type<string[]>().notNull().default([]),
});

export const showcaseBookings = pgTable("showcase_bookings", {
  addonIds: jsonb("addon_ids").$type<string[]>().notNull().default([]),
  channel: varchar("channel", { length: 16 }).notNull().default("direct"),
  checkIn: date("check_in").notNull(),
  checkOut: date("check_out").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  guestEmail: text("guest_email"),
  guestName: text("guest_name").notNull().default("Guest"),
  guestPhone: text("guest_phone"),
  guests: integer("guests").notNull(),
  id: uuid("id").defaultRandom().primaryKey(),
  promoCode: varchar("promo_code", { length: 32 }),
  roomId: uuid("room_id")
    .notNull()
    .references(() => showcaseRooms.id),
  sessionId: varchar("session_id", { length: 64 })
    .notNull()
    .references(() => showcaseSessions.id, { onDelete: "cascade" }),
  status: varchar("status", { length: 16 }).notNull().default("confirmed"),
  totalPrice: numeric("total_price", { precision: 10, scale: 2 }).notNull(),
});

export type ShowcaseRoom = typeof showcaseRooms.$inferSelect;
export type ShowcaseAddon = typeof showcaseAddons.$inferSelect;
export type ShowcasePromo = typeof showcasePromos.$inferSelect;
export type ShowcaseSeasonalRate = typeof showcaseSeasonalRates.$inferSelect;
export type ShowcaseBooking = typeof showcaseBookings.$inferSelect;
export type ShowcaseFaq = typeof showcaseFaq.$inferSelect;
